import { useState, type FormEvent } from "react";
import {
  clientCreateSchema,
  clientUpdateSchema,
  type ClientDetailRow,
} from "@themislex/shared";
import { ApiError, api } from "../api/client";
import { Button, CopyField, ErrorNote, Input, Modal, Textarea } from "./ui";

interface FormState {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  notes: string;
}

const EMPTY: FormState = {
  name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  notes: "",
};

/**
 * Create/edit a client. Creating a client also provisions their login: the
 * one-time setup link is displayed exactly once (only its hash is stored).
 * The inner form is keyed by client id and unmounts whenever the modal closes,
 * so its state is naturally fresh on every open.
 */
export function ClientFormModal({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: ClientDetailRow | null;
  onSaved: (client: ClientDetailRow) => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? "Edit client" : "Add client"}
      wide
    >
      <ClientForm key={initial?.id ?? "new"} initial={initial} onClose={onClose} onSaved={onSaved} />
    </Modal>
  );
}

function ClientForm({
  initial,
  onClose,
  onSaved,
}: {
  initial?: ClientDetailRow | null;
  onClose: () => void;
  onSaved: (client: ClientDetailRow) => void;
}) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<FormState>(() =>
    initial
      ? {
          name: initial.name,
          email: initial.email,
          phone: initial.phone ?? "",
          address: initial.address ?? "",
          city: initial.city ?? "",
          state: initial.state ?? "",
          pincode: initial.pincode ?? "",
          notes: initial.notes ?? "",
        }
      : EMPTY,
  );
  const [error, setError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [setupUrl, setSetupUrl] = useState<string | null>(null);

  const set = (key: keyof FormState) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setFieldErrors({});

    const schema = isEdit ? clientUpdateSchema : clientCreateSchema;
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setBusy(true);
    try {
      if (isEdit && initial) {
        const body = await api<{ client: ClientDetailRow }>(
          `/api/clients/${initial.id}`,
          { method: "PUT", json: parsed.data },
        );
        onSaved(body.client);
        onClose();
      } else {
        const body = await api<{ client: ClientDetailRow; setupUrl: string }>(
          "/api/clients",
          { method: "POST", json: parsed.data },
        );
        onSaved(body.client);
        setSetupUrl(body.setupUrl);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.issues) {
          const errors: Record<string, string> = {};
          for (const issue of err.issues) {
            if (!errors[issue.path]) errors[issue.path] = issue.message;
          }
          setFieldErrors(errors);
        }
      } else {
        setError("Could not save the client.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (setupUrl) {
    return (
      <div>
        <h3 className="font-heading text-base font-semibold">
          Client created — share their setup link
        </h3>
        <p className="mt-2 text-sm text-muted">
          This one-time link lets the client set their own password. It is shown
          only now and expires in 14 days — copy it somewhere safe before closing.
        </p>
        <div className="mt-4">
          <CopyField value={setupUrl} />
        </div>
        <p
          className="mt-3 rounded-md px-4 py-2 text-xs"
          style={{
            backgroundColor: "rgb(var(--tl-warning-bg))",
            color: "rgb(var(--tl-warning-fg))",
          }}
        >
          The link is displayed only once. If it is lost, generate a new one from
          the client's page.
        </p>
        <div className="mt-4 flex justify-end">
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      {error && (
        <div className="mb-4">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Input
            label="Full name"
            required
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
            error={fieldErrors.name}
          />
        </div>
        <Input
          label="Email (login username)"
          type="email"
          required={!isEdit}
          disabled={isEdit}
          value={form.email}
          onChange={(e) => set("email")(e.target.value)}
          error={fieldErrors.email}
          hint={isEdit ? "Email cannot be changed after setup." : undefined}
        />
        <Input
          label="Phone"
          value={form.phone}
          onChange={(e) => set("phone")(e.target.value)}
          error={fieldErrors.phone}
        />
        <div className="sm:col-span-2">
          <Input
            label="Address"
            value={form.address}
            onChange={(e) => set("address")(e.target.value)}
            error={fieldErrors.address}
          />
        </div>
        <Input
          label="City"
          value={form.city}
          onChange={(e) => set("city")(e.target.value)}
          error={fieldErrors.city}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="State"
            value={form.state}
            onChange={(e) => set("state")(e.target.value)}
            error={fieldErrors.state}
          />
          <Input
            label="PIN code"
            value={form.pincode}
            onChange={(e) => set("pincode")(e.target.value)}
            error={fieldErrors.pincode}
          />
        </div>
        <div className="sm:col-span-2">
          <Textarea
            label="Intake notes (private to chambers)"
            rows={4}
            value={form.notes}
            onChange={(e) => set("notes")(e.target.value)}
            error={fieldErrors.notes}
          />
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={busy}>
          {isEdit ? "Save changes" : "Create client"}
        </Button>
      </div>
    </form>
  );
}
